import { create } from 'zustand'
import { devtools } from 'zustand/middleware'

import { Client } from '../model/Client'
import { ClientRequest } from '../requests/ClientRequest'
import ClientEndpoints from '../endpoints/ClientEndpoints'

const NEW_ENTRY: number = -1

interface ClientState {
  // Core state
  client: Client | null
  currentClientId: number

  // Loading management
  waitingFor: number

  // Actions
  setClientId: (clientId: number) => void
  clearClient: () => void
  refresh: () => Promise<Client | null>
  createOrUpdate: (request: ClientRequest) => Promise<Client>

  // Loading helpers
  startLoading: () => void
  doneLoading: () => void

  // Computed getters
  isLoading: () => boolean
  isNewEntry: () => boolean

  // Private helper methods (exposed for internal use)
  create: (request: ClientRequest) => Promise<Client>
  update: (request: ClientRequest) => Promise<Client>
}

// Single global client store instance
export const useClientStore = create<ClientState>()(
  devtools(
    (set, get) => ({
      // Initial state
      client: null,
      currentClientId: NEW_ENTRY,
      waitingFor: 0,

      // Actions
      setClientId: (clientId: number) => {
        set({ currentClientId: clientId })
      },

      clearClient: () => {
        set({ 
          client: null, 
          currentClientId: NEW_ENTRY,
          waitingFor: 0 
        })
      },

      refresh: async () => {
        const { currentClientId, startLoading, doneLoading } = get()

        if (currentClientId === NEW_ENTRY) {
          // Cannot refresh a new entry, just clear the client
          set({ client: null })
          return null
        }

        startLoading()
        try {
          const client = await ClientEndpoints.getClient(currentClientId)
          set({ client })
          doneLoading()
          return client
        } catch (error) {
          console.error(error)
          doneLoading()
          throw error
        }
      },

      createOrUpdate: async (request: ClientRequest) => {
        const { currentClientId, create, update } = get()

        if (currentClientId === NEW_ENTRY) {
          return create(request)
        } else {
          return update(request)
        }
      },

      // Loading helpers
      startLoading: () =>
        set((state) => ({
          waitingFor: state.waitingFor + 1,
        })),

      doneLoading: () =>
        set((state) => ({
          waitingFor: Math.max(0, state.waitingFor - 1),
        })),

      // Computed getters
      isLoading: () => get().waitingFor > 0,

      isNewEntry: () => get().currentClientId === NEW_ENTRY,

      // Private helper methods
      create: async (request: ClientRequest) => {
        const { startLoading, doneLoading } = get()
        
        startLoading()
        try {
          const client = await ClientEndpoints.createClient(request)
          set({ 
            client, 
            currentClientId: client.id 
          })
          doneLoading()
          return client
        } catch (error) {
          console.error(error)
          doneLoading()
          throw error
        }
      },

      update: async (request: ClientRequest) => {
        const { currentClientId, startLoading, doneLoading } = get()

        startLoading()
        try {
          const client = await ClientEndpoints.editClient(currentClientId, request)
          set({ client })
          doneLoading()
          return client
        } catch (error) {
          console.error(error)
          doneLoading()
          throw error
        }
      },
    }),
    {
      name: 'client-store',
    }
  )
)
